import { useEffect, useState } from 'react';
import hash from 'object-hash';

import PAGES from '@/constants/pages';
import excludeInvalidFilterGrid, {
  ExcludeConditionFunction,
} from '@/features/filters/helpers/excludeInvalidFilterGrid';
import getFiltersForAutoSetup from '@/features/filters/helpers/getFiltersForAutoSetup';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { rootActions, rootSelectors } from '@/features/store';
import useCurrentPage from '@/hooks/useCurrentPage';
import { IFilterGrid } from '@/interfaces/constructor.interfaces';

import useFiltersData from './useFiltersData';

/**
 * Возвращает отредактированную сетку фильтров.
 * - Исключает фильтры без значений.
 * - Исключает фильтры с одним, единственным значением, если у соответствующих фильтров
 *   установлена настройка hideSingleOption
 * - Автоматически проставляет значение для фильтров с одной опцией,
 *    если у соответствующих фильтров установлена настройка autoSelectSingleOption.
 * @param filterGrid сетка фильтров
 * @returns грид, с отредактированными настройками
 */
export const useAdjustedFilterGrid = (filterGrid: IFilterGrid) => {
  const [adjustedFilterGrid, setAdjustedFilterGrid] = useState<IFilterGrid | null>(null);
  const dispatch = useAppDispatch();

  const page = useCurrentPage();

  const filtersData = useFiltersData();

  const filterChoice = useAppSelector(rootSelectors.choice.filtersChoiceSelector);
  const aggregateParameters = useAppSelector(
    rootSelectors.filters.aggregateParametersSelector,
  );
  const modelsFilterKey = useAppSelector(rootSelectors.config.modelsFilterKeySelector);

  const excludeConditionFilterItem: ExcludeConditionFunction = item => {
    const isModelsPage = page === PAGES.Models;

    // На странице с моделями необходимо скрыть фильтры по моделям
    if (isModelsPage && item.key === modelsFilterKey) {
      return true;
    }

    return false;
  };

  /**
   * Исключаем фильтры с одним, единственным значением, если
   * у конкретного фильтра установлена соответствующая настройка.
   */
  useEffect(() => {
    if (!filtersData.isSuccess || adjustedFilterGrid) {
      return;
    }

    const data = filtersData.data?.data;

    const adjustedGrid = excludeInvalidFilterGrid(
      filterGrid,
      data,
      excludeConditionFilterItem,
    );

    setAdjustedFilterGrid(adjustedGrid);
  }, [adjustedFilterGrid, filtersData, filterGrid]);

  /**
   * Проставляем значения для фильтров с одной опцией.
   * Если у соответствующих фильтров это указано в настройках.
   */
  useEffect(() => {
    // Если параметры query запроса совпадают с текущими параметрами для фильтров,
    // то данные по фильтрам считаются актуальными
    const isCurrentData =
      hash(aggregateParameters) === hash(filtersData?.originalArgs || {});

    if (!filtersData.isSuccess || !isCurrentData) {
      return;
    }

    const data = filtersData.data?.data;

    const filtersToSetup = getFiltersForAutoSetup(filterGrid, data, filterChoice);

    if (filtersToSetup.length) {
      dispatch(rootActions.choice.setFilters(filtersToSetup[0]));
    }
  }, [dispatch, filterChoice, filtersData, filterGrid, aggregateParameters]);

  return adjustedFilterGrid;
};

export default useAdjustedFilterGrid;
